import { useQuery, useQueryClient } from '@tanstack/react-query';
import { LogOut, Building2, Upload, UserPlus } from 'lucide-react';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import Logo from '@/assets/login_logo.svg';
import Avtar from '@/assets/member-assets/homepage/Avatar.svg';
import LogoutIcon from '@/assets/member-assets/homepage/Logout_Home.svg';
import { withErrorBoundary } from '@/components/common/ErrorBoundary';
import { LoadingState } from '@/components/common/feedback/LoadingState';
import MemberDefaultModel from '@/components/member/member-modals/MemberDefaultModel';
import { Button } from '@/components/ui/button';
import { logger } from '@/lib/logger';
import { signOutSession } from '@/lib/sign-out';
import { fetchUserContexts } from '@/pages/corporates/api';
import { BulkInviteEmployeesModal } from '@/pages/corporates/BulkInviteEmployeesModal';
import { InviteEmployeeModal } from '@/pages/corporates/InviteEmployeeModal';
import type { CorporateContext } from '@/pages/corporates/types';
import { RootState } from '@/redux/store';

type InviteModal = 'invite' | 'bulk';

export const CorporateHeaderContent: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isLogoutModalOpen, setLogoutModalOpen] = useState(false);
  const [activeModal, setActiveModal] = useState<InviteModal | null>(null);

  const firstName = useSelector((state: RootState) => state.user.first_name);
  const lastName = useSelector((state: RootState) => state.user.last_name);
  const username = useSelector((state: RootState) => state.user.username);
  const { data: contexts, isLoading: isLoadingContexts } = useQuery({
    queryKey: ['corporate-header-contexts'],
    queryFn: fetchUserContexts,
    staleTime: 5 * 60 * 1000,
  });
  const activeCorporate: CorporateContext | undefined = contexts?.corporate_contexts?.[0];

  const toTitleCase = (str: string | null): string => {
    if (!str) return '';
    return str
      .split(/[\s_]+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const formattedUserName =
    `${toTitleCase(firstName)} ${toTitleCase(lastName)}`.trim() || username || 'User';

  const handleConfirmLogout = async () => {
    try {
      await signOutSession({ queryClient });
      navigate('/login');
    } catch (error) {
      logger.error('Error signing out:', error);
      navigate('/login');
    } finally {
      setLogoutModalOpen(false);
    }
  };

  const isInviteModalOpen = activeModal !== null;

  const openInviteModal = () => setActiveModal('invite');
  const openBulkInviteModal = () => setActiveModal('bulk');
  const closeInviteModal = () => setActiveModal(null);

  const handleInviteSuccess = async () => {
    if (!activeCorporate) return;

    const corporateId = activeCorporate.corporate_id;
    try {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['corporate-enrollments-dashboard', corporateId],
        }),
        queryClient.invalidateQueries({ queryKey: ['corporate-dashboard', corporateId] }),
        queryClient.invalidateQueries({ queryKey: ['corporate-enrollments', corporateId] }),
      ]);
    } catch (error) {
      logger.error('Failed to refresh corporate invite data:', error);
      toast.error(
        'Invites were sent, but the dashboard could not refresh. Please reload the page.'
      );
    }
  };

  return (
    <>
      <header
        className="fixed top-[calc(var(--app-toolbar-height,0px)+12px)] left-4 right-4 z-30"
        data-testid="corporateHeader_root"
      >
        <div className="h-16 w-full bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80 border border-emerald-50 shadow-lg shadow-emerald-50/40 rounded-3xl px-4 lg:px-6 flex items-center justify-between">
          <div className="flex min-w-0 items-center gap-2 md:gap-6">
            <Link to="/corporate" className="flex items-center">
              <img src={Logo} alt="Logo" className="h-8" />
            </Link>
            {isLoadingContexts ? (
              <LoadingState text="Loading workspace…" />
            ) : (
              activeCorporate && (
                <div className="hidden md:flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-gray-50 flex items-center justify-center">
                    <Building2 className="h-5 w-5 text-gray-700" />
                  </div>
                  <div>
                    <p className="text-base font-semibold text-gray-900 leading-tight">
                      {activeCorporate.corporate_name}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                      {activeCorporate.corporate_code && (
                        <span>Code: {activeCorporate.corporate_code}</span>
                      )}
                      {activeCorporate.corporate_code && activeCorporate.program_name && (
                        <span className="hidden sm:inline">•</span>
                      )}
                      {activeCorporate.program_name && <span>{activeCorporate.program_name}</span>}
                    </div>
                  </div>
                </div>
              )
            )}
          </div>
          <div className="flex items-center gap-3">
            {isLoadingContexts ? (
              <LoadingState text="Loading workspace…" />
            ) : (
              activeCorporate && (
                <>
                  <Button
                    variant="outline"
                    className="gap-2"
                    disabled={isInviteModalOpen}
                    onClick={openBulkInviteModal}
                    data-testid="corporateHeader_inviteBtn"
                  >
                    <Upload className="h-4 w-4" />
                    <span className="hidden sm:inline">Bulk Invite</span>
                    <span className="sm:hidden">Bulk</span>
                  </Button>
                  <Button className="gap-2" disabled={isInviteModalOpen} onClick={openInviteModal}>
                    <UserPlus className="h-4 w-4" />
                    Invite
                  </Button>
                </>
              )
            )}
            <div className="flex items-center gap-3">
              <img
                className="hidden sm:block w-8 h-8 rounded-full"
                src={Avtar}
                alt={formattedUserName}
              />
              <div className="hidden md:block">
                <p className="text-sm font-medium text-gray-900 leading-tight">
                  {formattedUserName}
                </p>
                <p className="text-xs text-gray-500">Corporate Admin</p>
              </div>
              <button
                onClick={() => setLogoutModalOpen(true)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Logout"
                data-testid="corporateHeader_logoutBtn"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <MemberDefaultModel
        isOpen={isLogoutModalOpen}
        onClose={() => setLogoutModalOpen(false)}
        onConfirm={handleConfirmLogout}
        icon={LogoutIcon}
        title="Log out?"
        description="Are you sure you want to logout?"
        confirmButtonText="Log out"
        iconBgClassName="bg-gray-100"
        confirmButtonClassName="bg-black hover:bg-black"
      />

      {activeCorporate && (
        <>
          <InviteEmployeeModal
            isOpen={activeModal === 'invite'}
            onClose={closeInviteModal}
            corporateId={activeCorporate.corporate_id}
            onSuccess={handleInviteSuccess}
          />
          <BulkInviteEmployeesModal
            isOpen={activeModal === 'bulk'}
            onClose={closeInviteModal}
            corporateId={activeCorporate.corporate_id}
            corporateName={activeCorporate.corporate_name}
            onSuccess={handleInviteSuccess}
          />
        </>
      )}
    </>
  );
};

export const CorporateHeader = withErrorBoundary(CorporateHeaderContent);

export default CorporateHeader;
